import { Service } from '../Service';
/**
 * API for manipulating the 3D globe and its imagery and terrain.
 */
export class GlobeService extends Service {
    constructor(modules, navigationService) {
        super(modules);
        Object.defineProperty(this, "globe", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "navigationService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "imageryLayers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "terrainLayers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "imageryBaseMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "googleGlobe", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showImagery", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showTerrain", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.globe = this.renderer.getWorld().getGlobe();
        this.navigationService = navigationService;
        this.imageryLayers = [];
        this.terrainLayers = [];
        this.imageryBaseMap = 'satellite';
        // Load the globe tileset
        this.googleGlobe = null;
        this.renderer
            .getObjectFactory()
            .createTileset({ type: 'google' })
            .then(tileset => {
            this.renderer.getWorld().addObject(tileset);
            tileset.setVisible(false);
            this.googleGlobe = tileset;
        });
        this.showImagery = false;
        this.showTerrain = false;
        this.refreshGlobe();
    }
    /**
     * Get the imagery provider descriptor for the current base map.
     *
     * @returns
     */
    getBaseMap() {
        switch (this.imageryBaseMap) {
            case 'street':
                return { type: 'osm' };
            case 'satellite':
            default:
                return { type: 'bing', mapType: 'aerial' };
        }
    }
    /**
     * Refresh the globe with the current configuration state.
     *
     * @param onLoad Optional callback on globe load.
     */
    refreshGlobe(onLoad = () => { }, onError = () => { }) {
        const imagery = [];
        const terrain = [];
        if (this.showImagery) {
            imagery.push(this.getBaseMap());
            imagery.push(...this.imageryLayers);
        }
        if (this.showTerrain) {
            if (this.terrainLayers.length === 0) {
                terrain.push({ type: 'cesium', assetId: 1 });
            }
            else {
                terrain.push(...this.terrainLayers);
            }
        }
        if (!this.showImagery && !this.showTerrain) {
            this.navigationService.setUnderground(true);
            this.globe.setVisible(false);
            onLoad();
        }
        else {
            this.navigationService.setUnderground(false);
            this.globe.setTileLayers(imagery, terrain).then(onLoad).catch(onError);
            this.globe.setVisible(true);
        }
    }
    /**
     * Set the globe visibility mode.
     *
     * This will refresh the globe.
     *
     * @param mode
     * @returns
     */
    async setGlobeMode(mode) {
        this.showImagery = mode !== 'default' && mode !== 'google';
        this.showTerrain = mode === 'terrain';
        this.googleGlobe?.setVisible(mode === 'google');
        return new Promise((resolve, reject) => this.refreshGlobe(resolve, reject));
    }
    /**
     * Set the base imagery map.
     *
     * This will refresh the globe.
     *
     * @param map
     * @returns
     */
    async setImageryBaseMap(map) {
        this.imageryBaseMap = map;
        return new Promise((resolve, reject) => this.refreshGlobe(resolve, reject));
    }
    /**
     * Get the current globe visibility mode.
     *
     * @returns
     */
    getGlobeMode() {
        if (this.googleGlobe?.getVisible()) {
            return 'google';
        }
        else if (!this.showImagery && !this.showTerrain) {
            return 'default';
        }
        else if (this.showTerrain) {
            return 'terrain';
        }
        else {
            return 'imagery';
        }
    }
    /**
     * Get the currently set imagery base map.
     *
     * @returns
     */
    getImageryBaseMap() {
        return this.imageryBaseMap;
    }
    /**
     * Get the list of bounds for the currently active imagery.
     *
     * @returns
     */
    getImageryBounds() {
        return this.globe.getImageryBounds();
    }
    /**
     * Get the imagery layers currently displayed.
     *
     * @returns
     */
    getImageryLayers() {
        return [...this.imageryLayers];
    }
    /**
     * Get the terrain layers currently displayed.
     *
     * @returns
     */
    getTerrainLayers() {
        return [...this.terrainLayers];
    }
    /**
     * Get the attributions of the globe.
     *
     * @returns
     */
    getAttributions() {
        const googleAttributions = this.googleGlobe?.attributions ?? [];
        return googleAttributions.concat(this.globe.attributions);
    }
    /**
     * Set the imagery layers to be displayed.
     *
     * This will refresh the globe.
     *
     * WARNING: This is for internal use only. Use the ModelService to add new Imagery layers.
     *
     * @param imageryLayers New set of imagery layers on top of the base map
     * @returns
     */
    _setImageryLayers(imageryLayers) {
        this.imageryLayers = imageryLayers;
        return new Promise((resolve, reject) => this.refreshGlobe(resolve, reject));
    }
    /**
     * Set the terrain layers to be displayed.
     *
     * This will refresh the globe.
     *
     * WARNING: This is for internal use only. Use the ModelService to add new Terrain layers.
     *
     * @param terrainLayers New set of terrain layers
     * @returns
     */
    _setTerrainLayers(terrainLayers) {
        this.terrainLayers = terrainLayers;
        return new Promise((resolve, reject) => this.refreshGlobe(resolve, reject));
    }
    updateFrame(deltaTime) { }
}
